import React from 'react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from './ui/accordion';
import { Badge } from './ui/badge';
import { HelpCircle } from 'lucide-react';

const FAQ = () => {
  const faqs = [
    {
      question: "What is Effortrak?",
      answer: "Effortrak is a comprehensive tool designed for companies in the service industry where employees work on multiple projects. It helps in tracking effort, managing tasks, calculating the unit cost of projects, and includes a mobile-based self-attendance system."
    },
    {
      question: "How does Effortrak calculate the unit cost of a project?",
      answer: "Effortrak tracks the time and effort spent by each employee on a project, factoring in their salary brackets and overhead costs to provide an accurate unit cost for each project."
    },
    {
      question: "Can Effortrak handle multiple projects at once?",
      answer: "Yes, Effortrak is designed to manage multiple projects simultaneously, allowing employees to log their efforts across various tasks and projects efficiently."
    },
    {
      question: "How does the task assignment feature work?",
      answer: "Managers can easily assign tasks to employees on a daily basis. Employees can then log their activities, which are recorded in the system for reporting and analysis."
    },
    {
      question: "Where can Effortrak be hosted?",
      answer: "Effortrak can be hosted on the client's server or on a separate server that we can provide. The choice depends on the client's preferences and infrastructure."
    },
    {
      question: "Is the data stored in Effortrak secure?",
      answer: "Yes, all sensitive data, including client details, is stored in an encrypted format. Only admin users have access to view this data, and it cannot be decrypted by unauthorized users."
    },
    {
      question: "Who has access to the database of Effortrak?",
      answer: "The client has full control over the database, and we do not have access to it. This ensures that the client's data remains secure and private."
    },
    {
      question: "Can Effortrak be hosted on a cloud server?",
      answer: "Yes, Effortrak is designed to be cloud-hosted and can be easily deployed on a cloud server, either provided by the client or by us."
    },
    {
      question: "What are the server requirements for running Effortrak?",
      answer: "Effortrak runs on a Linux server with a MySQL database. For optimal performance, it is recommended to use a cPanel server, with shared hosting options from providers like GoDaddy.com or Optrahost.com being ideal. The server should have at least 1GB of storage space and 1GB of RAM to efficiently run the application."
    }
  ];

  return (
    <section id="faq" className="py-20 lg:py-32">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16 animate-fade-in">
          <Badge variant="secondary" className="mb-4 bg-primary/10 text-primary border-primary/20">
            <HelpCircle className="w-4 h-4 mr-1" />
            FAQ
          </Badge>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-bold text-foreground mb-6">
            Frequently Asked
            <span className="text-gradient-primary block">
              Questions
            </span>
          </h2>
          <p className="text-lg text-muted-foreground max-w-3xl mx-auto leading-relaxed">
            Everything you need to know about EfforTrak. Can't find the answer you're looking for? Please contact our team.
          </p>
        </div>

        {/* FAQ Accordion */}
        <div className="max-w-4xl mx-auto">
          <Accordion type="single" collapsible className="space-y-4">
            {faqs.map((faq, index) => (
              <AccordionItem 
                key={index} 
                value={`item-${index}`} 
                className="border-0 bg-card/60 backdrop-blur-sm rounded-lg shadow-card hover:shadow-elegant transition-shadow duration-300 animate-fade-in"
                style={{ animationDelay: `${index * 0.05}s` }}
              >
                <AccordionTrigger className="px-6 py-4 hover:no-underline text-left font-heading font-semibold text-foreground hover:text-primary transition-colors">
                  <span className="flex items-center space-x-3">
                    <span className="text-sm font-mono text-accent bg-accent/10 px-2 py-1 rounded">
                      {String(index + 1).padStart(2, '0')}
                    </span>
                    <span>{faq.question}</span>
                  </span>
                </AccordionTrigger>
                <AccordionContent className="px-6 pb-6 pt-0 text-muted-foreground leading-relaxed">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>

        {/* Additional Help */}
        <div className="text-center mt-16 animate-fade-in">
          <div className="bg-muted/50 rounded-2xl p-8 max-w-2xl mx-auto">
            <h3 className="text-xl font-heading font-semibold text-foreground mb-4">
              Still have questions?
            </h3>
            <p className="text-muted-foreground mb-6">
              Our team is here to help you get started with EfforTrak and answer any questions you might have.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="mailto:info@keylines.net"
                className="inline-flex items-center justify-center px-6 py-3 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors font-medium"
              >
                Email Support
              </a>
              <a
                href="https://wa.me/919330109091"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center px-6 py-3 border border-border rounded-lg hover:bg-muted transition-colors font-medium"
              >
                WhatsApp Chat
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FAQ;